/**
 * Tile map data layer — the foundation of every combat arena.
 *
 * # What lives here
 *
 * The pure-functional data layer for combat tile maps. Three categories of
 * exports:
 *
 *   1. **Engine constants** — `TILE_LOGICAL_WIDTH`, `TILE_LOGICAL_HEIGHT`,
 *      `TILE_ANCHOR`, plus map-size caps that mirror the Zod schema.
 *   2. **Tile-type enum** — `TileType` (frozen const object) and
 *      `TileTypeValue` (literal-union type derived from it).
 *   3. **Factory + helpers** — `createTileMap` (deep-frozen output),
 *      builder shape + `freezeTileMap` (used by generators in Layers 2-5),
 *      read helpers (`inBounds`, `tileIndex`, `getTile`, `getTileSafe`,
 *      `isBlocked`), construction-time mutators (`setTile`, `setBlocked`),
 *      and anchor-conversion math between tile coords and fixed-point world
 *      coords.
 *
 * # Design principles
 *
 *   1. **Pure data, no side effects.** No DOM, no fetch, no setTimeout, no
 *      Math.random. The map subsystem is fully cross-runtime — runs in
 *      browser and Node identically.
 *
 *   2. **Deterministic.** All map construction is driven by `generationSeed`
 *      (a bigint) routed through `SeededRng` in higher layers. This file
 *      exposes no random behavior; it's data + math.
 *
 *   3. **Bit-exact arithmetic.** Anchor-conversion math uses integer
 *      multiplication only. No `Math.sin/cos/sqrt` of float arguments.
 *      The fixed-point integer discipline locked in v2.9 + v2.10 extends
 *      here without exception.
 *
 *   4. **Deep-frozen factory output.** `createTileMap` returns a TileMap
 *      where every nested array and object is frozen. Mutation throws in
 *      strict mode. This is the runtime equivalent of the static-analysis
 *      test that enforces no-float-math: enforced invariants, not requested
 *      ones. Critical for anti-cheat replay correctness — a renderer or
 *      input-handler cannot accidentally diverge the client from the
 *      server-side replay.
 *
 *   5. **Builder pattern for generators.** Layers 2-5 (BSP, drunkard's-walk,
 *      pathfinding, spawn placement) take a `TileMapBuilder` (mutable),
 *      populate it during construction, and pass it back to the factory
 *      which freezes it before returning to the consumer. The builder
 *      shape is internal to the map subsystem.
 *
 * # Schema contract
 *
 * Every TileMap produced by `createTileMap` must validate against
 * `TileMapSchema` in `save/schema.ts`. The factory enforces:
 *   - `tiles.length === width * height`
 *   - `collision.length === width * height`
 *   - `width, height` in [1, 256]
 *   - `tileSize` in [1, 1024]
 *   - `generationSeed` is stringified bigint
 *   - Sparse-metadata caps (spawnPoints ≤ 256, exits ≤ 64, hazards ≤ 256)
 *
 * Schema-level refinements that go BEYOND the type system are mirrored
 * here as runtime asserts in `validateBuilder`. A factory that produces
 * an invalid TileMap is a programming error and throws immediately,
 * NOT silently produces a TileMap that fails save validation later.
 *
 * # Anchor convention
 *
 * Tile anchor: bottom-center of the visible diamond.
 *
 * For a tile at integer coordinate `(tileX, tileY)`:
 *   - World X anchor = (tileX + 0.5) * TILE_LOGICAL_WIDTH
 *   - World Y anchor = (tileY + 1)   * TILE_LOGICAL_HEIGHT
 *
 * The Y offset is `+1` rather than `+0.5` because bottom-center anchor
 * means the visible diamond's anchor sits at the tile's bottom edge.
 * X is centered horizontally (+0.5).
 *
 * Both values are then scaled to fixed-point (multiplied by
 * `FIXED_POINT_SCALE = 1000`) to live in the same arithmetic space as
 * combat positions/velocities.
 *
 * # Overflow safety
 *
 * Max tile coord: 255 (256-tile maps). Max world coord in fixed-point:
 *   255 * TILE_LOGICAL_WIDTH * FIXED_POINT_SCALE
 *     = 255 * 1628 * 1000
 *     = 415,140,000
 *
 * Well below `Number.MAX_SAFE_INTEGER` (~9 × 10^15). Safe.
 *
 * **However**, `fpMul(worldX, worldX)` for distance-squared between two
 * world-space positions on a 256-wide map could exceed
 * `FP_SAFE_MUL_OPERAND_MAX` (~94M). This is a Phase 3 (Combat Reducer)
 * concern, not Phase 1. Combat code that takes squared distances between
 * far-apart entities should either work in tile-relative coords, or
 * accept that 256-tile maps are above the safe-multiplication ceiling
 * for that operation. Flagged for Phase 3.
 *
 * @see {@link ../state/types.ts} — TileMap interface (this factory's output)
 * @see {@link ../save/schema.ts} — TileMapSchema (validation gate)
 * @see {@link ../combat/fixedPoint.ts} — FIXED_POINT_SCALE
 */

import type {
  BiomeId,
  TileMap,
  SpawnPoint,
  ExitZone,
  HazardZone,
} from '../state/types';
import { FIXED_POINT_SCALE } from '../combat/fixedPoint';

// ─────────────────────────────────────────────────────────────────────────────
// Engine constants (LOCKED v2.11)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Logical pixel width of one tile, matching the maxparata Compact biome
 * pack's GroundTile dimensions. Verified May 6, 2026 by per-category
 * filename recon (12 GroundTile PNGs at uniform 1628×217).
 *
 * LOCKED v2.11. Do not change without a deliberate engine-revision
 * session — every biome pack subsequently sourced must match this
 * dimension or be scaled to it at the renderer boundary.
 */
export const TILE_LOGICAL_WIDTH = 1628;

/**
 * Logical pixel height of one tile. Pairs with `TILE_LOGICAL_WIDTH`
 * for the ~7.5:1 shallow isometric aspect ratio (Riftbreaker-style
 * perspective, achieved via 2D sprites NOT 3D rendering).
 *
 * LOCKED v2.11.
 */
export const TILE_LOGICAL_HEIGHT = 217;

/**
 * Tile anchor convention. Bottom-center of the visible diamond is the
 * standard isometric anchor for both rendering and pathfinding alignment.
 *
 * LOCKED v2.11.
 */
export const TILE_ANCHOR = 'bottom-center' as const;

/**
 * Fixed-point world coordinate of one full tile width.
 *
 * Used in anchor-conversion math: `tileX * TILE_WORLD_WIDTH_FP` gives the
 * fixed-point world X of the left edge of tile `tileX`.
 *
 * Value: `1628 * 1000 = 1_628_000`. Compile-time constant.
 */
export const TILE_WORLD_WIDTH_FP = TILE_LOGICAL_WIDTH * FIXED_POINT_SCALE;

/**
 * Fixed-point world coordinate of one full tile height.
 *
 * Value: `217 * 1000 = 217_000`. Compile-time constant.
 */
export const TILE_WORLD_HEIGHT_FP = TILE_LOGICAL_HEIGHT * FIXED_POINT_SCALE;

// ─────────────────────────────────────────────────────────────────────────────
// Map-size caps (mirror Zod TileMapSchema in save/schema.ts)
// ─────────────────────────────────────────────────────────────────────────────

/** Minimum width or height in tiles. */
export const MAP_DIM_MIN = 1;

/**
 * Maximum width or height in tiles. Mirrors the Zod schema cap.
 * Tampered saves declaring 100000 × 100000 maps are rejected at validation.
 * 256 × 256 = 65,536 tiles, comfortably above any realistic combat arena.
 */
export const MAP_DIM_MAX = 256;

/**
 * Maximum logical pixel size of one tile. Mirrors Zod schema cap.
 *
 * Bumped from 1024 (Phase C draft cap) to 4096 in Phase 1 to accommodate
 * the locked TILE_LOGICAL_WIDTH = 1628. The 4096 ceiling still defends
 * against tampered saves declaring tileSize = 100000.
 *
 * Note: `tileSize` is a single-scalar legacy field. Real tile dimensions
 * are non-square (1628×217). The renderer in Phase 2 uses
 * TILE_LOGICAL_WIDTH and TILE_LOGICAL_HEIGHT constants directly,
 * not the `tileSize` field.
 */
export const TILE_SIZE_MAX = 4096;

/** Maximum number of spawn points per map. Mirrors Zod schema cap. */
export const SPAWN_POINTS_MAX = 256;

/** Maximum number of exit zones per map. Mirrors Zod schema cap. */
export const EXITS_MAX = 64;

/** Maximum number of hazard zones per map. Mirrors Zod schema cap. */
export const HAZARDS_MAX = 256;

// ─────────────────────────────────────────────────────────────────────────────
// TileType — frozen const object with literal-union type
//
// Why frozen const object instead of `const enum`: the latter has known
// fragility under Vite/esbuild + isolatedModules: true, and esbuild's
// transpile-only mode doesn't inline const enums at all. Frozen const
// object survives any transpiler identically, gives the same compile-time
// type narrowing, and is runtime-iterable for validators. See Phase 1
// design lock decision (May 6, 2026).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tile-type values used in `TileMap.tiles[]` arrays.
 *
 * The on-disk representation is non-negative integers (per the Zod
 * schema's `z.array(z.number().int().nonnegative())`). This object is
 * the engine-level contract for what those integers mean.
 *
 *   - `Floor` — passable ground tile, base biome surface
 *   - `FloorPatch` — passable ground tile with decorative overlay
 *   - `Prop` — non-passable obstacle (rock, crater, building corner)
 *   - `Door` — passable transition tile (logical exit/entry)
 *   - `Hazard` — passable but damaging tile (handled by HazardZone metadata)
 *
 * **No `wall` for v1.** Per the locked open-plan Brotato/Vampire-Survivors
 * combat design, perimeter framing is via Buildings (Prop) and internal
 * cover via Props. Walls may be added in a future revision; doing so
 * requires extending this object AND coordinating with Phase 2 renderer
 * + Phase 4 pathfinding.
 */
export const TileType = Object.freeze({
  Floor: 0,
  FloorPatch: 1,
  Prop: 2,
  Door: 3,
  Hazard: 4,
} as const);

/**
 * Literal-union type of all valid tile-type integers.
 *
 * `TileTypeValue` is `0 | 1 | 2 | 3 | 4` at compile time. Used by code
 * that reads tile arrays and wants type narrowing on the integer value.
 */
export type TileTypeValue = (typeof TileType)[keyof typeof TileType];

/**
 * Set of all known tile-type integer values, for runtime validation.
 *
 * Useful when constructing tile arrays from external sources: check
 * `KNOWN_TILE_TYPES.has(value)` before assigning into the tiles array.
 * The Zod schema only validates `nonnegative integer`, not membership in
 * the known type set, so this set fills the gap for stricter callers.
 */
export const KNOWN_TILE_TYPES: ReadonlySet<number> = Object.freeze(
  new Set<number>(Object.values(TileType)),
);

// ─────────────────────────────────────────────────────────────────────────────
// TileMapBuilder — mutable shape used during construction
//
// Layers 2-5 (BSP, drunkard's-walk, pathfinding, spawn placement) consume
// a builder, mutate its arrays in place, and return it. The factory wraps
// `createTileMapBuilder` + caller's generation pass + `freezeTileMap`.
//
// The builder shape is structurally identical to `TileMap` but without
// the `readonly` field-level modifier — i.e. fields can be reassigned
// during construction (rare, but useful for some patterns). Arrays are
// mutable via standard methods.
//
// After `freezeTileMap`, the builder becomes a frozen TileMap and its
// arrays cannot be mutated further. Mutation attempts throw in strict
// mode (which all our code runs in via esbuild output).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Mutable shape used during tile-map construction. Layers 2-5 generators
 * accept this and populate it; the factory freezes it before returning
 * to consumers.
 *
 * **Internal to the map subsystem.** External code should consume only
 * `TileMap` (the frozen public type from `state/types.ts`).
 */
export interface TileMapBuilder {
  width: number;
  height: number;
  tileSize: number;
  tiles: number[];
  collision: boolean[];
  spawnPoints: SpawnPoint[];
  exits: ExitZone[];
  hazards: HazardZone[];
  biomeId: BiomeId;
  generationSeed: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory parameters
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parameters for `createTileMap` and `createTileMapBuilder`.
 *
 *   - `width`, `height` — required, in [MAP_DIM_MIN, MAP_DIM_MAX]
 *   - `biomeId` — required, drives tileset selection at render time
 *   - `generationSeed` — required, the bigint seed that will drive
 *     procedural generation in higher layers. Stored stringified per
 *     the Zod schema's bigint-as-string convention.
 *   - `tileSize` — optional, defaults to TILE_LOGICAL_WIDTH. The renderer
 *     uses this for pixel projection; combat math doesn't.
 *   - `fillTile` — optional, defaults to TileType.Floor. Initial value
 *     for every cell of `tiles[]`.
 *   - `fillCollision` — optional, defaults to false. Initial value for
 *     every cell of `collision[]`.
 */
export interface CreateTileMapParams {
  readonly width: number;
  readonly height: number;
  readonly biomeId: BiomeId;
  readonly generationSeed: bigint;
  readonly tileSize?: number;
  readonly fillTile?: number;
  readonly fillCollision?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Validation — assert builder satisfies schema-level refinements
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Validate that a builder satisfies all `TileMapSchema` constraints that
 * are not type-system-expressible (the schema's `.refine()` clauses, plus
 * range bounds and array caps).
 *
 * This is called by `freezeTileMap` before freezing. A builder that fails
 * here is a programming error in the generator code; we throw immediately
 * with a descriptive message rather than producing a TileMap that fails
 * save validation later (which would be a confusing far-from-cause error).
 *
 * Mirrors the constraints in `save/schema.ts:TileMapSchema`.
 *
 * @throws RangeError if any constraint is violated
 */
function validateBuilder(b: TileMapBuilder): void {
  // Dimensions
  if (
    !Number.isInteger(b.width) ||
    b.width < MAP_DIM_MIN ||
    b.width > MAP_DIM_MAX
  ) {
    throw new RangeError(
      `TileMap width must be integer in [${MAP_DIM_MIN}, ${MAP_DIM_MAX}], got ${b.width}`,
    );
  }
  if (
    !Number.isInteger(b.height) ||
    b.height < MAP_DIM_MIN ||
    b.height > MAP_DIM_MAX
  ) {
    throw new RangeError(
      `TileMap height must be integer in [${MAP_DIM_MIN}, ${MAP_DIM_MAX}], got ${b.height}`,
    );
  }

  // Tile size
  if (
    !Number.isInteger(b.tileSize) ||
    b.tileSize < 1 ||
    b.tileSize > TILE_SIZE_MAX
  ) {
    throw new RangeError(
      `TileMap tileSize must be integer in [1, ${TILE_SIZE_MAX}], got ${b.tileSize}`,
    );
  }

  // Array length refinements (the .refine() clauses in TileMapSchema)
  const expectedLen = b.width * b.height;
  if (b.tiles.length !== expectedLen) {
    throw new RangeError(
      `TileMap tiles array length (${b.tiles.length}) must equal width × height (${expectedLen})`,
    );
  }
  if (b.collision.length !== expectedLen) {
    throw new RangeError(
      `TileMap collision array length (${b.collision.length}) must equal width × height (${expectedLen})`,
    );
  }

  // Tile values: non-negative integers (Zod-level constraint).
  // We don't enforce KNOWN_TILE_TYPES membership here because future tile
  // types may be added; the Zod schema also allows any non-negative int.
  for (let i = 0; i < b.tiles.length; i++) {
    const v = b.tiles[i]!;
    if (!Number.isInteger(v) || v < 0) {
      throw new RangeError(
        `TileMap tiles[${i}] must be non-negative integer, got ${v}`,
      );
    }
  }

  // Sparse-metadata caps
  if (b.spawnPoints.length > SPAWN_POINTS_MAX) {
    throw new RangeError(
      `TileMap spawnPoints (${b.spawnPoints.length}) exceeds cap ${SPAWN_POINTS_MAX}`,
    );
  }
  if (b.exits.length > EXITS_MAX) {
    throw new RangeError(
      `TileMap exits (${b.exits.length}) exceeds cap ${EXITS_MAX}`,
    );
  }
  if (b.hazards.length > HAZARDS_MAX) {
    throw new RangeError(
      `TileMap hazards (${b.hazards.length}) exceeds cap ${HAZARDS_MAX}`,
    );
  }

  // Generation seed: stringified bigint (matches Zod's regex /^-?\d+$/)
  if (!/^-?\d+$/.test(b.generationSeed)) {
    throw new RangeError(
      `TileMap generationSeed must be a stringified bigint (matches /^-?\\d+$/), got ${b.generationSeed}`,
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Builder factory — mutable output for generator pipelines
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Construct a `TileMapBuilder` filled with default tile and collision
 * values. The builder is mutable; generators in Layers 2-5 populate it.
 * Use `freezeTileMap` to convert to a frozen `TileMap` before returning
 * to consumers.
 *
 * No validation is performed here — `freezeTileMap` validates before
 * freezing, so generator code can mutate freely during construction.
 *
 * @internal Generators in Layers 2-5 use this directly. External code
 *   should call `createTileMap` instead.
 */
export function createTileMapBuilder(
  params: CreateTileMapParams,
): TileMapBuilder {
  const tileSize = params.tileSize ?? TILE_LOGICAL_WIDTH;
  const fillTile = params.fillTile ?? TileType.Floor;
  const fillCollision = params.fillCollision ?? false;

  // Eagerly validate dimensions BEFORE allocating arrays — a tampered
  // params object declaring width = 100000 would otherwise allocate
  // ~10 GB before failing. Validate first.
  if (
    !Number.isInteger(params.width) ||
    params.width < MAP_DIM_MIN ||
    params.width > MAP_DIM_MAX
  ) {
    throw new RangeError(
      `createTileMapBuilder: width must be integer in [${MAP_DIM_MIN}, ${MAP_DIM_MAX}], got ${params.width}`,
    );
  }
  if (
    !Number.isInteger(params.height) ||
    params.height < MAP_DIM_MIN ||
    params.height > MAP_DIM_MAX
  ) {
    throw new RangeError(
      `createTileMapBuilder: height must be integer in [${MAP_DIM_MIN}, ${MAP_DIM_MAX}], got ${params.height}`,
    );
  }

  const cellCount = params.width * params.height;
  const tiles = new Array<number>(cellCount).fill(fillTile);
  const collision = new Array<boolean>(cellCount).fill(fillCollision);

  return {
    width: params.width,
    height: params.height,
    tileSize,
    tiles,
    collision,
    spawnPoints: [],
    exits: [],
    hazards: [],
    biomeId: params.biomeId,
    generationSeed: params.generationSeed.toString(),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Deep-freeze — convert builder to immutable TileMap
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Freeze a `TileMapBuilder` and return it as an immutable `TileMap`.
 *
 * Validates the builder against schema-level refinements first; throws
 * if any constraint is violated. After successful freeze, the returned
 * value passes through `TileMapSchema.parse()` without further transformation.
 *
 * # What gets frozen
 *
 *   - The outer object — field references can't be reassigned.
 *   - `tiles` array — element values can't be mutated; length can't change.
 *   - `collision` array — same.
 *   - `spawnPoints`, `exits`, `hazards` arrays — same.
 *   - Each individual `SpawnPoint`, `ExitZone`, `HazardZone` object inside
 *     the sparse-metadata arrays.
 *
 * Mutation attempts throw `TypeError` in strict mode (all our code runs
 * in strict mode via esbuild output).
 *
 * # Why freeze beyond the v2.10 outer-readonly pattern
 *
 * The v2.10 lock (outer field-level readonly stays, inner array readonly
 * drops) was about TypeScript ergonomics at the Zod boundary. Runtime
 * freeze is a different concern: enforcing that consumers (renderer,
 * reducers, pathfinder) cannot mutate the map after construction. This
 * is critical for:
 *
 *   - Anti-cheat replay correctness (server-side replay must see the
 *     same map bytes the client saw at run start)
 *   - Determinism (pathfinder seeing mid-search mutation could enter
 *     cycles or produce non-deterministic paths)
 *   - Migration safety (SAVE_VERSION 2→3 sees schema-pure inputs)
 *   - React/Zustand rendering (reference-equality change detection
 *     requires immutable updates)
 *
 * Same enforcement-not-convention philosophy as the no-float-math static
 * analysis test: invariants are enforced, not requested.
 *
 * @param builder Mutable builder produced by `createTileMapBuilder`,
 *   typically populated by Layer 2-5 generators
 * @returns Deep-frozen `TileMap` ready for consumption
 * @throws RangeError if the builder fails any schema-level refinement
 */
export function freezeTileMap(builder: TileMapBuilder): TileMap {
  validateBuilder(builder);

  // Freeze sparse-metadata items individually — Object.freeze on the array
  // alone wouldn't freeze the contained objects.
  for (const sp of builder.spawnPoints) Object.freeze(sp);
  for (const ex of builder.exits) Object.freeze(ex);
  for (const hz of builder.hazards) Object.freeze(hz);

  Object.freeze(builder.tiles);
  Object.freeze(builder.collision);
  Object.freeze(builder.spawnPoints);
  Object.freeze(builder.exits);
  Object.freeze(builder.hazards);

  // The cast is safe: TileMapBuilder is structurally identical to TileMap
  // except for the field-level readonly modifier. Object.freeze gives us
  // the runtime equivalent of those readonly markers.
  return Object.freeze(builder) as unknown as TileMap;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public factory — single-call construction for simple cases
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Construct a deep-frozen `TileMap` filled with default tile and collision
 * values. Callers that need procedural generation should use
 * `createTileMapBuilder` directly, run their generators against it, then
 * call `freezeTileMap` themselves.
 *
 * This single-call form is for tests, fixtures, and any case where the
 * default fill is sufficient.
 *
 * @returns Deep-frozen TileMap that passes `TileMapSchema.parse()`
 */
export function createTileMap(params: CreateTileMapParams): TileMap {
  const builder = createTileMapBuilder(params);
  return freezeTileMap(builder);
}

// ─────────────────────────────────────────────────────────────────────────────
// Read helpers — bounds, indexing, tile/collision access
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Test whether `(x, y)` is a valid tile coordinate within the map.
 *
 * @returns true if `0 ≤ x < width` AND `0 ≤ y < height`
 */
export function inBounds(map: TileMap, x: number, y: number): boolean {
  return x >= 0 && x < map.width && y >= 0 && y < map.height;
}

/**
 * Convert tile coordinate `(x, y)` to flat-array index using row-major
 * ordering (`y * width + x`).
 *
 * **Does not check bounds.** Callers are responsible for validating
 * with `inBounds` first if `(x, y)` may be out of range.
 *
 * @returns Index into `map.tiles[]` and `map.collision[]`
 */
export function tileIndex(map: TileMap, x: number, y: number): number {
  return y * map.width + x;
}

/**
 * Read the tile-type integer at `(x, y)`.
 *
 * @returns Tile-type integer (typically a `TileTypeValue` but the schema
 *   permits any non-negative integer)
 * @throws RangeError if `(x, y)` is out of bounds
 */
export function getTile(map: TileMap, x: number, y: number): number {
  if (!inBounds(map, x, y)) {
    throw new RangeError(
      `getTile: (${x}, ${y}) out of bounds for ${map.width}×${map.height} map`,
    );
  }
  return map.tiles[tileIndex(map, x, y)]!;
}

/**
 * Read the tile-type integer at `(x, y)`, returning `null` for
 * out-of-bounds coordinates.
 *
 * Useful in iteration patterns where bounds-checking each access is
 * cleaner as a null check than a try/catch.
 *
 * @returns Tile-type integer or `null` if out of bounds
 */
export function getTileSafe(
  map: TileMap,
  x: number,
  y: number,
): number | null {
  if (!inBounds(map, x, y)) return null;
  return map.tiles[tileIndex(map, x, y)]!;
}

/**
 * Test whether the tile at `(x, y)` blocks movement.
 *
 * Out-of-bounds coordinates are treated as blocked — this is the
 * conventional pathfinder semantic (entities can't walk off the map).
 *
 * @returns true if the tile blocks movement, OR if `(x, y)` is OOB
 */
export function isBlocked(map: TileMap, x: number, y: number): boolean {
  if (!inBounds(map, x, y)) return true;
  return map.collision[tileIndex(map, x, y)]!;
}

// ─────────────────────────────────────────────────────────────────────────────
// Construction-time mutators — for use ONLY during builder construction
//
// These are exported for Layers 2-5 generator code. They mutate arrays
// in place, which is correct ONLY before `freezeTileMap` is called.
// After freeze, these throw TypeError in strict mode (frozen array
// cannot be assigned to).
//
// Type signature accepts `TileMap` rather than `TileMapBuilder` because
// callers in tests and fixtures sometimes work with the post-freeze type.
// At runtime these will throw on a frozen map; that's the correct
// failure mode (loud, immediate, points at the offending line).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Set the tile-type integer at `(x, y)`.
 *
 * **Construction-only.** After `freezeTileMap`, this throws because the
 * underlying array is frozen. That throw is the correct failure mode —
 * mutation of a finalized map is a programming error.
 *
 * @throws RangeError if `(x, y)` is out of bounds
 * @throws RangeError if `tileId` is not a non-negative integer
 * @throws TypeError if the map has been frozen (strict mode)
 */
export function setTile(
  map: TileMapBuilder | TileMap,
  x: number,
  y: number,
  tileId: number,
): void {
  // Bounds check uses the read-side helper which works on both shapes.
  if (!inBounds(map as TileMap, x, y)) {
    throw new RangeError(
      `setTile: (${x}, ${y}) out of bounds for ${map.width}×${map.height} map`,
    );
  }
  if (!Number.isInteger(tileId) || tileId < 0) {
    throw new RangeError(
      `setTile: tileId must be non-negative integer, got ${tileId}`,
    );
  }
  // Cast to mutable for assignment. If the array is frozen, this throws
  // TypeError in strict mode — which is exactly what we want.
  (map.tiles as number[])[tileIndex(map as TileMap, x, y)] = tileId;
}

/**
 * Set the collision flag at `(x, y)`.
 *
 * **Construction-only.** Same caveats as `setTile`.
 *
 * @throws RangeError if `(x, y)` is out of bounds
 * @throws TypeError if the map has been frozen (strict mode)
 */
export function setBlocked(
  map: TileMapBuilder | TileMap,
  x: number,
  y: number,
  blocked: boolean,
): void {
  if (!inBounds(map as TileMap, x, y)) {
    throw new RangeError(
      `setBlocked: (${x}, ${y}) out of bounds for ${map.width}×${map.height} map`,
    );
  }
  (map.collision as boolean[])[tileIndex(map as TileMap, x, y)] = blocked;
}

// ─────────────────────────────────────────────────────────────────────────────
// Anchor-conversion math — tile coords ↔ fixed-point world coords
//
// All four functions are pure integer arithmetic. No floats. No
// Math.sin/cos/sqrt of float arguments. Bit-exact across machines.
//
// Convention (LOCKED v2.11):
//   - Anchor: bottom-center of the visible diamond
//   - World X anchor of tile (tx, ty) = (tx + 0.5) * TILE_LOGICAL_WIDTH
//   - World Y anchor of tile (tx, ty) = (ty + 1)   * TILE_LOGICAL_HEIGHT
//
// Everything is then scaled by FIXED_POINT_SCALE for fixed-point storage.
// To avoid the 0.5 (float) in the multiplication, we compute:
//   worldX_fp = tx * TILE_WORLD_WIDTH_FP + (TILE_WORLD_WIDTH_FP / 2 | 0)
// where /2 | 0 is integer division (TILE_WORLD_WIDTH_FP is even because
// TILE_LOGICAL_WIDTH * 1000 = 1_628_000, which is even).
//
// Reverse direction uses Math.floor for integer division.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Half a tile width in fixed-point. Used to position the X anchor at
 * tile center horizontally.
 *
 * `1628 * 1000 / 2 = 814_000`. Compile-time constant, integer-exact
 * because `TILE_LOGICAL_WIDTH * FIXED_POINT_SCALE` is even.
 */
const TILE_WORLD_HALF_WIDTH_FP = TILE_WORLD_WIDTH_FP >> 1;

/**
 * Convert integer tile X coordinate to fixed-point world X anchor.
 *
 * Returns the X coordinate of the tile's bottom-center anchor in
 * fixed-point world units.
 *
 * Negative or out-of-bounds tile coords convert mathematically (you'll
 * get a negative or large result); callers must check `inBounds`
 * separately if needed.
 *
 * @param tileX Integer tile X coordinate
 * @returns Fixed-point world X coordinate of the tile's anchor
 */
export function tileXToWorldFp(tileX: number): number {
  return tileX * TILE_WORLD_WIDTH_FP + TILE_WORLD_HALF_WIDTH_FP;
}

/**
 * Convert integer tile Y coordinate to fixed-point world Y anchor.
 *
 * Returns the Y coordinate of the tile's bottom-center anchor in
 * fixed-point world units. Bottom anchor means Y is offset by a full
 * tile height (not half).
 *
 * @param tileY Integer tile Y coordinate
 * @returns Fixed-point world Y coordinate of the tile's anchor
 */
export function tileYToWorldFp(tileY: number): number {
  return (tileY + 1) * TILE_WORLD_HEIGHT_FP;
}

/**
 * Convert fixed-point world X coordinate back to integer tile X.
 *
 * Uses `Math.floor` (integer division). Negative inputs return negative
 * tile coords; world coords beyond the map's right edge return tile coords
 * ≥ width. Callers must check `inBounds` if the result must be valid.
 *
 * Round-trip property: `worldFpToTileX(tileXToWorldFp(t)) === t` for all
 * non-negative integers `t`.
 *
 * @param worldXFp Fixed-point world X coordinate
 * @returns Integer tile X coordinate
 */
export function worldFpToTileX(worldXFp: number): number {
  return Math.floor(worldXFp / TILE_WORLD_WIDTH_FP);
}

/**
 * Convert fixed-point world Y coordinate back to integer tile Y.
 *
 * The bottom-center anchor convention means a world Y at the tile's
 * anchor sits at the BOTTOM edge of the tile. To recover tile coord
 * we subtract 1 from the floor-division result so the round-trip
 * property holds:
 *
 *   tileYToWorldFp(t) = (t + 1) * TILE_WORLD_HEIGHT_FP
 *   worldFpToTileY(...) = floor(... / TILE_WORLD_HEIGHT_FP) - 1 = t
 *
 * Negative or out-of-range world Y produces correspondingly out-of-range
 * tile Y; callers must check `inBounds` if needed.
 *
 * @param worldYFp Fixed-point world Y coordinate
 * @returns Integer tile Y coordinate
 */
export function worldFpToTileY(worldYFp: number): number {
  return Math.floor(worldYFp / TILE_WORLD_HEIGHT_FP) - 1;
}
